// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import { validateRequestPath } from './api-util';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const requestPath = validateRequestPath(path);

  const url = `${apiBaseUrl()}${requestPath}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : body ? { body } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);
        e.status = res.status;
        e.message = data?.error || data?.message || res.statusText || 'Request failed';
        e.statusText = res.statusText;
        e.data = data;

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

const get = (path, options = {}) => {
  return request(path, {
    ...options,
    method: methods.GET,
  });
};

const put = (path, body, options = {}) => {
  return request(path, {
    ...options,
    method: methods.PUT,
    body,
  });
};

const del = (path, options = {}) => {
  return request(path, {
    ...options,
    method: methods.DELETE,
  });
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

// Provision Supabase RBAC group based on userType after signup.
// Idempotent — safe to call multiple times as a fallback.
export const provisionUser = () => {
  return post('/api/auth/provision-user', {});
};

// Check if user can be deleted and then delete the user. Endpoint logic
// must be modified to accommodate the transaction processes used in
// the marketplace.
export const deleteUserAccount = body => {
  return post('/api/delete-account', body);
};

export const loginWithSupabase = body => {
  return post('/api/auth/login', body);
};

export const signupWithSupabase = body => {
  return post('/api/auth/signup', body);
};

export const logoutFromSupabase = () => {
  return post('/api/auth/logout', {});
};

export const recoverPasswordSupabase = body => {
  return post('/api/auth/recover-password', body);
};

export const verifyEmailSupabase = body => {
  return post('/api/auth/verify-email', body);
};

export const resetPasswordSupabase = body => {
  return post('/api/auth/reset-password', body);
};

export const updatePasswordSupabase = body => {
  return post('/api/auth/update-password', body);
};

// Solver proposal history
export const listSolverProposalRuns = () => {
  return get('/api/solver/proposals');
};

export const deleteSolverProposalRun = id => {
  return del(`/api/solver/proposals/${id}`);
};

// Child management endpoints
export const createChild = body => {
  return post('/api/children/create', body);
};

export const getChildren = () => {
  return get('/api/children');
};

export const updateChild = body => {
  return put('/api/children/update', body);
};

export const uploadChildAvatar = (childId, file) => {
  const formData = new FormData();
  formData.append('childId', childId);
  formData.append('image', file);

  const url = `${apiBaseUrl()}/api/children/upload-avatar`;
  return window
    .fetch(url, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    })
    .then(res => {
      if (res.status >= 400) {
        return res.json().then(data => {
          let e = new Error();
          e = Object.assign(e, data);
          throw e;
        });
      }
      return res.json();
    });
};

export const removeChildAvatar = childId => {
  return post('/api/children/remove-avatar', { childId });
};

export const deleteChild = body => {
  return post('/api/children/delete', body);
};

export const shareChild = body => {
  return post('/api/children/share', body);
};

export const getChildInvitations = () => {
  return get('/api/children/invitations');
};

export const acceptChildInvitation = body => {
  return post('/api/children/invitations/accept', body);
};

export const declineChildInvitation = body => {
  return post('/api/children/invitations/decline', body);
};

// CalDAV calendar endpoints (Davis proxy)
export const createChildCalendar = body => {
  return post('/api/calendar/create', body);
};

export const getCalendars = () => {
  return get('/api/calendar/list');
};

export const getCalendarEvents = calendarId => {
  const value =
    calendarId && typeof calendarId === 'object' ? calendarId.calendarId : calendarId;
  return get(`/api/calendar/events/${value}`);
};

export const createCalendarEvent = body => {
  return post('/api/calendar/events', body);
};

export const updateCalendarEvent = body => {
  return post('/api/calendar/events/update', body);
};

export const deleteCalendarEvent = body => {
  return post('/api/calendar/events/delete', body);
};

// Activity management
export const createActivity = body => {
  return post('/api/activities/create', body);
};

export const getActivities = status => {
  const value = status && typeof status === 'object' ? status.status : status;
  const qs = value ? `?${new URLSearchParams({ status: value }).toString()}` : '';
  return get(`/api/activities${qs}`);
};

export const getActivity = id => {
  const value = id && typeof id === 'object' ? id.id : id;
  return get(`/api/activities/${value}`);
};

// Fetch the full Sharetribe listing for an activity (vendor-only, works for unpublished)
export const getActivityListing = id => {
  const value = id && typeof id === 'object' ? id.id : id;
  return get(`/api/activities/${value}/listing`);
};

export const updateActivity = body => {
  return put('/api/activities/update', body);
};

export const deleteActivity = body => {
  return post('/api/activities/delete', body);
};

export const archiveActivity = body => {
  return post('/api/activities/archive', body);
};

export const uploadActivityImage = (activityId, file) => {
  const formData = new FormData();
  formData.append('activityId', activityId);
  formData.append('image', file);

  const url = `${apiBaseUrl()}/api/activities/upload-image`;
  return window
    .fetch(url, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    })
    .then(res => {
      if (res.status >= 400) {
        return res.json().then(data => {
          let e = new Error();
          e = Object.assign(e, data);
          throw e;
        });
      }
      return res.json();
    });
};

// Session management
export const createSession = body => {
  return post('/api/sessions/create', body);
};

export const getSessionsForActivity = activityId => {
  const value =
    activityId && typeof activityId === 'object' ? activityId.activityId : activityId;
  return get(`/api/sessions/${value}`);
};

export const updateSession = body => {
  return put('/api/sessions/update', body);
};

export const deleteSession = body => {
  return post('/api/sessions/delete', body);
};

export const publishSession = body => {
  return post('/api/sessions/publish', body);
};

export const getVendorSessions = () => {
  return get('/api/sessions/vendor');
};

// Child likes
export const likeActivity = body => {
  return post('/api/children/like', body);
};

export const unlikeActivity = body => {
  return post('/api/children/unlike', body);
};

export const getChildLikes = childId => {
  const value = childId && typeof childId === 'object' ? childId.childId : childId;
  return get(`/api/children/${value}/likes`);
};

// Child connections
export const addChildConnection = body => {
  return post('/api/children/connections/add', body);
};

export const removeChildConnection = body => {
  return post('/api/children/connections/remove', body);
};

export const getChildConnections = childId => {
  const value = childId && typeof childId === 'object' ? childId.childId : childId;
  return get(`/api/children/${value}/connections`);
};

// Child interests
export const setChildInterests = body => {
  return put('/api/children/interests', body);
};

export const getChildInterests = childId => {
  const value = childId && typeof childId === 'object' ? childId.childId : childId;
  return get(`/api/children/${value}/interests`);
};

// Cart
export const createNewCart = body => {
  return post('/api/cart/create', body || {});
};

export const fetchCart = cartId => {
  const value = cartId && typeof cartId === 'object' ? cartId.cartId : cartId;
  return get(`/api/cart/${value}`);
};

export const createCartWithLines = lines => {
  const payload = Array.isArray(lines) ? { lines } : lines && typeof lines === 'object' ? lines : { lines };
  return post('/api/cart/create-with-lines', { lines: payload.lines });
};

export const addCartLines = (body, maybeLines) => {
  const payload = body && typeof body === 'object' ? body : { cartId: body, lines: maybeLines };
  return post(`/api/cart/${payload.cartId}/add-lines`, {
    lines: payload.lines,
  });
};

export const updateCartLines = (body, maybeLines) => {
  const payload = body && typeof body === 'object' ? body : { cartId: body, lines: maybeLines };
  return post(`/api/cart/${payload.cartId}/update-lines`, {
    lines: payload.lines,
  });
};

export const removeCartLines = (body, maybeLineIds) => {
  const payload =
    body && typeof body === 'object' ? body : { cartId: body, lineIds: maybeLineIds };
  return post(`/api/cart/${payload.cartId}/remove-lines`, {
    lineIds: payload.lineIds,
  });
};

// Activity search
export const searchActivities = (params = {}) => {
  const query = params && typeof params === 'object' ? params : {};
  const qs = new URLSearchParams(query).toString();
  return get(`/api/activities/search${qs ? `?${qs}` : ''}`);
};

// Cart checkout (Stripe Separate Charges and Transfers)
export const validateCart = body => {
  return post('/api/cart/validate', body);
};

export const cartCheckout = body => {
  return post('/api/cart/checkout', body);
};

export const cartConfirmCheckout = body => {
  return post('/api/cart/confirm-checkout', body);
};

// Solver (CampScout planner)
export const solverSolve = body => {
  return post('/api/solver/solve', body);
};

export const solverHealth = () => {
  return get('/api/solver/health');
};

export const solverValidate = body => {
  return post('/api/solver/validate', body);
};

export const solverAcceptProposal = body => {
  return post('/api/solver/accept-proposal', body);
};

// Stripe
export const stripeSetupCard = body => {
  return post('/api/stripe/setup-card', body || {});
};
